<template>
    <div ref="outerElementRef" class="settings-page__languages">
        <div class="languages__items">
            <button
                v-for="(item, idx) in langs"
                :id="getId(item.code)"
                :ref="setItemRef"
                :key="item.code"
                :data-keydown="getId(langs[idx + 1]?.code)"
                :data-keyup="getId(langs[idx - 1]?.code)"
                class="languages__item"
                @click="() => btnHandler(item.code)"
                @focus="onFocus()"
                @blur="onBlur"
            >
                <div>
                    {{ $t(item.translatedName) }}
                </div>
                <common-icon v-if="item.code === currentLanguageCode"  :icon-name="'IconCheck'" />
            </button>
        </div>
    </div>
</template>
<script lang="ts" setup>
import { ref, type Ref, onMounted, onBeforeUnmount, computed, onBeforeUpdate } from "vue";
import { type ISettingsLocales, LangCodes } from "@/common/types/languages"
import { useLanguagesUtils } from "@/composables/useLanguagesUtils";
import { useFocusedComponents } from '@/composables/useFocusedComponents';

const emit = defineEmits(['back-to-navigation'])
const isRegKeyListeners: Ref<boolean> = ref(false);
const currentLanguageCode: Ref<LangCodes> = ref(LangCodes.SYSTEM);
let btnsRef: Array<Element | ComponentPublicInstance | null> = [];

const { $navigator } = useNuxtApp();
const { setLocale, settingsLocales, getCurrLangCode } = useLanguagesUtils();
const { setFocused, unsetFocused, outerElementRef } = useFocusedComponents('SettingsLanguages');

const langs = computed((): ISettingsLocales[] => settingsLocales());

const setItemRef = (el: Element | ComponentPublicInstance | null) => {
    if (el) {
        btnsRef.push(el)
    }
}
const getId = (code: string): string => {
    return `languages-${code}`;
}
const goToBack = (): void => {
    emit("back-to-navigation");
}
const onKeyLeft = (event: KeyboardEvent): void => {
    const element = event.target as HTMLElement | null;
    if (element?.getAttribute("data-type") === "modal-button") {
        return;
    }
    goToBack();
}
const unregisterKeyListeners = (): void => {
    if (isRegKeyListeners.value) {
        $navigator.off("keypress:left", onKeyLeft);
        $navigator.off("keypress:back", goToBack);
        isRegKeyListeners.value = false;
    }
}
const unsetSelfFocused = (): void => {
    unregisterKeyListeners();
    unsetFocused();
}
const registerKeyListeners = (): void => {
    if (!isRegKeyListeners.value) {
        $navigator.on("keypress:left", onKeyLeft);
        $navigator.on("keypress:back", goToBack);
        isRegKeyListeners.value = true;
    }
}
const onFocus = (): void => {
    registerKeyListeners();
}
const onBlur = (): void => {
    unregisterKeyListeners();
}
const btnHandler = (code: LangCodes): void => {
    setLocale(code);
    currentLanguageCode.value = code;
}
const setSelfFocused = (): void => {
    const currentLanguageIndex = langs.value.findIndex(({ code }) => code === currentLanguageCode.value)
    const catcherFocus = btnsRef[currentLanguageIndex];
    if (catcherFocus instanceof HTMLElement) {
        catcherFocus.focus();
        setFocused();
    }
}
onBeforeUpdate(() => {
    btnsRef = []
});
onMounted(async () => {
    currentLanguageCode.value = getCurrLangCode();
    setSelfFocused();
});
onBeforeUnmount(() => {
    unsetSelfFocused();
});
</script>
<style lang="scss">
    @import url("styles.scss");
</style>